<template>
  <el-card shadow="never" class="aui-card--fill">
    <div class="category-mapping-page">
      <el-row :gutter="16" style="height: 100%;">
        <!-- 左侧：资源子类列表 -->
        <el-col :span="9">
          <div class="panel-header">
            <span class="panel-title">资源子类</span>
            <el-input
              v-model="sonDictSearch"
              placeholder="搜索子类名称"
              clearable
              size="small"
              style="width: 160px;"
              prefix-icon="el-icon-search"
            ></el-input>
          </div>
          <el-table
            ref="sonDictTable"
            :data="filteredSonDictList"
            border
            highlight-current-row
            size="small"
            height="620"
            v-loading="sonDictLoading"
            @current-change="handleSonDictSelect"
            style="width: 100%; margin-top: 8px;"
          >
            <el-table-column prop="dictCode" label="ID" width="70" align="center"></el-table-column>
            <el-table-column prop="dictName" label="子类名称" align="center"></el-table-column>
            <el-table-column label="父分类" align="center">
              <template slot-scope="scope">{{ dictMap[scope.row.pid] || '-' }}</template>
            </el-table-column>
            <el-table-column label="映射" width="90" align="center">
              <template slot-scope="scope">
                <template v-if="selectedSonDict && scope.row.dictCode === selectedSonDict.dictCode">
                  <el-tag type="warning" size="mini" effect="plain">
                    选择中({{ selectedUpstreamIds.length }})
                  </el-tag>
                </template>
                <template v-else-if="mappedSonDictMap[scope.row.dictCode]">
                  <el-tag type="success" size="mini" effect="plain">
                    已映射({{ mappedSonDictMap[scope.row.dictCode] }})
                  </el-tag>
                </template>
                <template v-else>
                  <el-tag type="info" size="mini" effect="plain">未映射</el-tag>
                </template>
              </template>
            </el-table-column>
          </el-table>
        </el-col>

        <!-- 右侧：上游分类映射 -->
        <el-col :span="15">
          <template v-if="selectedSonDict">
            <div class="panel-header">
              <span class="panel-title">
                上游分类映射
                <span class="selected-label">— {{ selectedSonDict.dictName }}</span>
              </span>
              <div>
                <el-button size="small" @click="clearSelection">清空</el-button>
                <el-button type="primary" size="small" :loading="saving" @click="saveMapping">保存映射</el-button>
              </div>
            </div>
            <div class="selected-count" style="margin: 6px 0;">
              已选 <b>{{ selectedUpstreamIds.length }}</b> 个 &nbsp;|&nbsp;
              原有映射 <b>{{ originalMappingIds.length }}</b> 个
            </div>
            <div v-loading="upstreamLoading || mappingLoading" class="upstream-tree">
              <div
                v-for="parent in upstreamCategories"
                :key="parent.id"
                class="upstream-group"
              >
                <div class="group-header">
                  <el-checkbox
                    :indeterminate="isIndeterminate(parent)"
                    :value="isAllSelected(parent)"
                    @change="toggleParent(parent, $event)"
                  >{{ parent.name }}</el-checkbox>
                </div>
                <div class="group-children">
                  <el-checkbox
                    v-for="child in parent.children"
                    :key="child.id"
                    :label="child.id"
                    :value="!!selectedUpstreamIdsSet[child.id]"
                    @change="onChildChange(child.id, $event)"
                    class="child-checkbox"
                  >
                    {{ child.name }}
                    <span class="resource-count">({{ child.resource_count || 0 }})</span>
                  </el-checkbox>
                </div>
              </div>
            </div>
          </template>
          <div v-else class="empty-tip">
            <i class="el-icon-arrow-left"></i> 请在左侧选择一个资源子类
          </div>
        </el-col>
      </el-row>
    </div>
  </el-card>
</template>

<script>
export default {
  name: 'CategoryMapping',
  data () {
    return {
      // 左侧
      sonDictLoading: false,
      sonDictList: [],
      sonDictSearch: '',
      dictMap: {},
      selectedSonDict: null,

      // 右侧
      upstreamLoading: false,
      upstreamCategories: [],

      // 已有映射（用于标记左侧"已映射(n)"）
      mappedSonDictMap: {},

      // 当前选中子类的映射
      mappingLoading: false,
      currentMappings: [],       // [{id, mappingCategoryId, ...}]
      originalMappingIds: [],    // 原始已映射的上游 child ID 列表
      selectedUpstreamIdsSet: {}, // { [childId]: boolean } 用于复选框绑定
      saving: false,

      // 上游 child → parent 的快速查找
      childParentMap: {}
    }
  },
  computed: {
    filteredSonDictList () {
      if (!this.sonDictSearch) return this.sonDictList
      const kw = this.sonDictSearch.toLowerCase()
      return this.sonDictList.filter(item =>
        item.dictName && item.dictName.toLowerCase().includes(kw)
      )
    },
    selectedUpstreamIds () {
      return Object.keys(this.selectedUpstreamIdsSet)
        .filter(id => this.selectedUpstreamIdsSet[id])
        .map(Number)
    }
  },
  created () {
    this.loadSonDictList()
    this.loadUpstreamCategories()
    this.loadAllMappedIds()
  },
  methods: {
    // ---- 初始化数据 ----
    loadSonDictList () {
      this.sonDictLoading = true
      this.$http.get('/sondict/sondict/page', { params: { page: 1, limit: 999 } }).then(({ data: res }) => {
        if (res.code !== 0) return
        this.sonDictList = res.data.list || []
        // 同时加载父分类 map
        this.$http.get('/dict/dict/page', { params: { limit: 999 } }).then(({ data: r }) => {
          if (r.code === 0) {
            ;(r.data.list || []).forEach(item => {
              this.$set(this.dictMap, item.dictCode, item.dictName)
            })
          }
        })
      }).finally(() => { this.sonDictLoading = false })
    },

    loadUpstreamCategories () {
      this.upstreamLoading = true
      this.$http.get('/category/long_video/resource_category').then(({ data }) => {
        const list = Array.isArray(data) ? data : (data.data || [])
        this.upstreamCategories = list
        // 构建 childParentMap
        list.forEach(parent => {
          (parent.children || []).forEach(child => {
            this.childParentMap[child.id] = parent
          })
        })
      }).finally(() => { this.upstreamLoading = false })
    },

    loadAllMappedIds () {
      this.$http.get('/api/category-mappings', { params: { mappingType: 'long_video' } }).then(({ data: res }) => {
        const list = res.data || res || []
        const countMap = {}
        list.forEach(m => {
          if (m.systemCategoryId) {
            const id = Number(m.systemCategoryId)
            countMap[id] = (countMap[id] || 0) + 1
          }
        })
        this.mappedSonDictMap = countMap
      })
    },

    // ---- 左侧选中 ----
    handleSonDictSelect (row) {
      if (!row) return
      this.selectedSonDict = row
      this.loadMappingsForSelected(row)
    },

    loadMappingsForSelected (sonDict) {
      this.mappingLoading = true
      this.selectedUpstreamIdsSet = {}
      this.currentMappings = []
      this.originalMappingIds = []

      this.$http.get('/api/category-mappings', {
        params: { mappingType: 'long_video', systemCategoryId: sonDict.dictCode }
      }).then(({ data: res }) => {
        const list = res.data || res || []
        this.currentMappings = list

        const newSet = {}
        list.forEach(m => {
          if (m.mappingCategoryId) {
            newSet[m.mappingCategoryId] = true
          }
        })
        this.selectedUpstreamIdsSet = newSet
        this.originalMappingIds = list.map(m => Number(m.mappingCategoryId))
      }).finally(() => { this.mappingLoading = false })
    },

    // ---- 右侧复选框 ----
    onChildChange (childId, val) {
      this.$set(this.selectedUpstreamIdsSet, childId, val)
    },

    isAllSelected (parent) {
      if (!parent.children || !parent.children.length) return false
      return parent.children.every(c => this.selectedUpstreamIdsSet[c.id])
    },

    isIndeterminate (parent) {
      if (!parent.children || !parent.children.length) return false
      const checkedCount = parent.children.filter(c => this.selectedUpstreamIdsSet[c.id]).length
      return checkedCount > 0 && checkedCount < parent.children.length
    },

    toggleParent (parent, val) {
      const newSet = { ...this.selectedUpstreamIdsSet }
      ;(parent.children || []).forEach(c => {
        newSet[c.id] = val
      })
      this.selectedUpstreamIdsSet = newSet
    },

    clearSelection () {
      this.selectedUpstreamIdsSet = {}
    },

    // ---- 保存映射 ----
    async saveMapping () {
      if (!this.selectedSonDict) return

      const currentIds = new Set(this.selectedUpstreamIds)
      const originalIds = new Set(this.originalMappingIds)

      const toCreate = [...currentIds].filter(id => !originalIds.has(id))
      const toDelete = this.currentMappings
        .filter(m => !currentIds.has(Number(m.mappingCategoryId)))
        .map(m => m.id)

      this.saving = true
      try {
        // 批量删除
        if (toDelete.length > 0) {
          await this.$http.delete('/api/category-mappings/batch', { data: toDelete })
        }

        // 逐条创建
        const sonDict = this.selectedSonDict
        const parentName = this.dictMap[sonDict.pid] || ''
        for (const childId of toCreate) {
          const parent = this.childParentMap[childId]
          if (!parent) continue
          const child = (parent.children || []).find(c => c.id === childId)
          if (!child) continue
          await this.$http.post('/api/category-mappings', {
            systemCategoryId: sonDict.dictCode,
            systemCategoryName: sonDict.dictName,
            systemCategoryParentId: sonDict.pid || 0,
            systemCategoryParentName: parentName,
            isLevelOne: false,
            mappingCategoryId: child.id,
            mappingCategoryName: child.name,
            mappingCategoryParentId: parent.id,
            mappingCategoryParentName: parent.name,
            isMappingLevelOne: false,
            mappingType: 'long_video'
          })
        }

        this.$message.success('映射保存成功')
        // 刷新当前映射 & 左侧标记
        this.loadMappingsForSelected(sonDict)
        this.loadAllMappedIds()
      } catch (e) {
        this.$message.error('保存失败，请重试')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.category-mapping-page {
  padding: 4px;
}
.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}
.panel-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}
.selected-label {
  font-weight: 400;
  color: #409eff;
  font-size: 13px;
}
.selected-count {
  font-size: 12px;
  color: #909399;
}
.upstream-tree {
  height: 620px;
  overflow-y: auto;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 8px;
}
.upstream-group {
  margin-bottom: 12px;
}
.group-header {
  background: #f5f7fa;
  padding: 6px 10px;
  border-radius: 4px;
  margin-bottom: 6px;
  font-weight: 600;
}
.group-children {
  padding-left: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px 0;
}
.child-checkbox {
  width: 33%;
  margin-right: 0 !important;
}
.resource-count {
  font-size: 11px;
  color: #c0c4cc;
}
.empty-tip {
  height: 620px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #909399;
  font-size: 14px;
  gap: 6px;
}
</style>
